<template>
  <div
    v-if="isVisible"
    class="modal fade show d-block"
    tabindex="-1"
    style="background: rgba(0, 0, 0, 0.5)"
    @click.self="hide"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-dark fs-5">Nueva Solicitud de Soporte</h5>
          <button
            type="button"
            class="btn-close btn-close-black"
            @click="hide"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submit">
            <div class="mb-3">
              <label for="action" class="form-label">Tipo de Solicitud</label>
              <select
                class="form-select"
                id="action"
                v-model="form.action"
                @change="onActionChange"
                required
              >
                <option value="">Seleccione...</option>
                <option value="COMPROBANTE">Comprobante de Inscripción</option>
                <option value="CERTIFICADO">
                  Certificado de Participación
                </option>
                <option value="CONTRASEÑA">Restablecer Contraseña</option>
              </select>
            </div>

            <component
              v-if="form.action"
              :is="currentComponent"
              :form-data="form"
              @data-verified="onDataVerified"
            />
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="hide">
            Cancelar
          </button>
          <button
            v-if="canSubmit"
            type="button"
            class="btn btn-primary"
            @click="submit"
            :disabled="submitting"
          >
            {{ submitting ? "Guardando..." : "Crear Solicitud" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject } from "vue";
import axios from "@/includes/connection";
import { toast } from "vue3-toastify";
import ComprobanteForm from "./forms/ComprobantForm.vue";
import CertificadoForm from "./forms/CertificateForm.vue";
import PasswordForm from "./forms/PasswordForm.vue";

const swal = inject("$swal");

const emit = defineEmits(["ticket-created"]);

const isVisible = ref(false);
const submitting = ref(false);
const dataVerified = ref(false);

const form = ref({
  action: "",
  participant_document: "",
  event_id: null,
  user_id: null,
  enrollment_base_id: null,
  event_enrollment_id: null,
  attendance_id: null,
  user_type: null,
  detail: "",
});

const currentComponent = computed(() => {
  const components = {
    COMPROBANTE: ComprobanteForm,
    CERTIFICADO: CertificadoForm,
    CONTRASEÑA: PasswordForm,
  };
  return components[form.value.action];
});

const canSubmit = computed(() => {
  return dataVerified.value;
});

function onActionChange() {
  form.value.participant_document = "";
  form.value.event_id = null;
  form.value.user_id = null;
  form.value.enrollment_base_id = null;
  form.value.event_enrollment_id = null;
  form.value.attendance_id = null;
  form.value.user_type = null;
  form.value.detail = "";
  dataVerified.value = false;
}

function onDataVerified(data) {
  Object.assign(form.value, data);
  dataVerified.value = true;
}

async function submit() {
  if (!canSubmit.value) {
    swal.fire({
      title: "Formulario incompleto",
      text: `Complete todos los campos para continuar.`,
      icon: "warning",
    });
    return;
  }

  submitting.value = true;

  try {
    const ticketData = {
      participant_document: form.value.participant_document,
      event_id: form.value.event_id,
      enrollment_base_id: form.value.enrollment_base_id,
      event_enrollment_id: form.value.event_enrollment_id,
      attendance_id: form.value.attendance_id,
      user_type: form.value.user_type,
      action: form.value.action,
      detail: form.value.detail,
    };

    const response = await axios.post("/ticket/create", ticketData);

    if (response.data) {
      if (
        form.value.action === "CONTRASEÑA" &&
        response.data.requestCount > 1
      ) {
        swal.fire({
          title: "Atención",
          text: `Este usuario ya ha solicitado restablecer su contraseña ${response.data.requestCount} veces.`,
          icon: "warning",
        });
      } else {
        swal.fire({
          title: "Ticket creado",
          text: "Ticket creado exitosamente.",
          icon: "success",
        });
      }

      emit("ticket-created", response.data);
      hide();
    }
  } catch (error) {
    console.error("Error", error);
    if (error.response?.status === 409) {
      swal.fire({
        title: "Conflicto",
        text: "Ya existe un ticket con esos datos.",
        icon: "error",
      });
    } else if (error.response?.data?.message) {
      swal.fire({
        title: "Error",
        text: error.response.data.message,
        icon: "error",
      });
    } else {
      swal.fire({
        title: "Error",
        text: "Error al crear el ticket",
        icon: "error",
      });
    }
  } finally {
    submitting.value = false;
  }
}

function resetForm() {
  form.value = {
    action: "",
    participant_document: "",
    event_id: null,
    user_id: null,
    enrollment_base_id: null,
    event_enrollment_id: null,
    attendance_id: null,
    user_type: null,
    detail: "",
  };
  dataVerified.value = false;
}

const show = () => {
  resetForm();
  isVisible.value = true;
  document.body.style.overflow = "hidden";
};

const hide = () => {
  isVisible.value = false;
  document.body.style.overflow = "";
  resetForm();
};

defineExpose({ show, hide });
</script>
